import React, { useCallback, useEffect, useState } from "react";

import { useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FilterOption from "@mapmycustomers/shared/enum/fieldModel/FilterOption";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import {
  EntityTypesSupportedByMapsPage,
  MapFilterEntityType,
} from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { FilterOptionValue } from "@mapmycustomers/shared/types/fieldModel/IFilterConfig";
import FilterModel, {
  SimpleCondition,
} from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import { ModifiedMapFilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/MapFilterModel";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";

import ButtonLink from "@app/component/ButtonLink";
import FieldColumn from "@app/component/filtering/components/FieldColumn";
import FilterSelector from "@app/component/filtering/components/FilterSelector";
import getMapEntityTypeDisplayName from "@app/component/filtering/components/MapFilterButton/utils/getMapEntityTypeDisplayName";
import FilterOperatorAction from "@app/types/FilterOperatorAction";
import useAnalytics from "@app/util/contexts/useAnalytics";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";
import loggingService from "@app/util/logging";

import EntityNotVisible from "./components/EntityNotVisible";
import Footer from "./components/Footer";
import styles from "./EntityTypeFilters.module.scss";

const onlyFilterableOnMap = ({ hasFeature }: IField) => hasFeature(FieldFeature.FILTERABLE_ON_MAP);

interface Props {
  checkInFieldsVisibility: boolean;
  context: string;
  entityType: MapFilterEntityType;
  filteredFields: IField[];
  filterModel: Partial<FilterModel>;
  filterOptions?: Partial<Record<FilterOption, FilterOptionValue>>;
  focusedField?: string;
  hiddenFields?: IField[];
  onChange?: (
    callback: (previousFilterModel: ModifiedMapFilterModel) => ModifiedMapFilterModel
  ) => void;
  visibleEntities: EntityTypesSupportedByMapsPage[];
}

const EntityTypeFilters: React.FC<Props> = ({
  checkInFieldsVisibility,
  context,
  entityType,
  filteredFields,
  filterModel,
  filterOptions,
  focusedField,
  hiddenFields,
  onChange,
  visibleEntities,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const fieldModel = getFieldModelByEntityType(entityType);

  const handleClearClick = useCallback(() => {
    onChange?.((filters) => ({ ...filters, [entityType]: undefined }));
    analytics.clicked(["Clear"]);
  }, [analytics, entityType, onChange]);

  const [newFilterShown, setNewFilterShown] = useState<boolean>(filteredFields.length === 0);
  useEffect(() => {
    setNewFilterShown(filteredFields.length === 0);
  }, [filteredFields]);

  const handleRemoveFilter = useDynamicCallback((field: IField) => {
    onChange?.((filters) => ({
      ...filters,
      [entityType]: { ...filters[entityType], [field.filterName]: undefined },
    }));
    analytics.clicked(["Remove rule"]);
  });

  const handleChangeFilter = useDynamicCallback((field: IField, condition: SimpleCondition) => {
    onChange?.((filters) => {
      const updatedFilterModel = { ...filters[entityType] };
      // This removal is needed to guarantee that the new field will be added to the end of the list.
      // The only reason it could appear not at the end was when such field has been added once and then
      // removed. It was kept with an undefined value is such case, preserving its previous position.
      if (
        field.filterName in updatedFilterModel &&
        updatedFilterModel[field.filterName] === undefined
      ) {
        delete updatedFilterModel[field.filterName];
      }
      updatedFilterModel[field.filterName] = condition;
      loggingService.debug(`onChange ${entityType} filter `, {
        new: updatedFilterModel,
        old: filterModel,
      });
      return { ...filters, [entityType]: updatedFilterModel };
    });
  });

  const handleAddFilterClick = useDynamicCallback(() => {
    setNewFilterShown(true);
    analytics?.clicked(["Add Filter"]);
  });

  const handleChangeField = useCallback(
    (oldField, newField, newCondition) => {
      handleChangeFilter(newField, newCondition);
    },
    [handleChangeFilter]
  );

  const filterOperators: FilterOperatorAction = useCallback(
    (field, defaultOperators) =>
      field.hasFeature(FieldFeature.FUNNEL_FIELD)
        ? defaultOperators.filter(({ operator }) => operator !== FilterOperator.EQUALS)
        : defaultOperators,
    []
  );

  return (
    <div className={styles.container}>
      <div className={styles.titleContainer}>
        <span className={styles.title}>{getMapEntityTypeDisplayName(intl, entityType)}</span>
        <ButtonLink className={styles.clear} onClick={handleClearClick}>
          {intl.formatMessage({
            id: "map.filters.clearEntityTypeFilters",
            defaultMessage: "Clear",
            description:
              "Clear button to remove all filters for given entity type in filters on Map page",
          })}
        </ButtonLink>
      </div>
      <div className={styles.filters}>
        {entityType !== "universal" && !visibleEntities.includes(entityType) ? (
          <EntityNotVisible entityType={entityType} />
        ) : (
          <>
            <div className={styles.filtersGrid}>
              {filteredFields.map((field, index) => (
                <FilterSelector
                  checkInFieldsVisibility={checkInFieldsVisibility}
                  context={context}
                  entityType={entityType}
                  field={field}
                  fieldModel={fieldModel}
                  filterModel={filterModel}
                  filterOperators={filterOperators}
                  filterOptions={filterOptions}
                  firstFilter={index === 0}
                  focused={focusedField === field.name}
                  hiddenFields={hiddenFields}
                  key={field.name}
                  onChange={handleChangeFilter}
                  onRemove={handleRemoveFilter}
                  shouldShowField={entityType !== "universal" ? onlyFilterableOnMap : undefined}
                  showColumnGroups={entityType !== "universal"}
                />
              ))}
              {newFilterShown && (
                <FieldColumn
                  checkInFieldsVisibility={checkInFieldsVisibility}
                  className={styles.fieldName}
                  context={context}
                  entityType={entityType}
                  fieldModel={fieldModel}
                  filterModel={filterModel}
                  filterOperators={filterOperators}
                  firstFilter={filteredFields.length === 0}
                  hiddenFields={hiddenFields}
                  onChange={handleChangeField}
                  shouldShowField={entityType !== "universal" ? onlyFilterableOnMap : undefined}
                  showColumnGroups={entityType !== "universal"}
                />
              )}
            </div>
            {filteredFields.length > 0 && (
              <Footer
                addDisabled={newFilterShown}
                entityType={entityType}
                onAdd={handleAddFilterClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default EntityTypeFilters;
